class NostoCollection {
    constructor(productListSelector) {
        this._productListSelector = productListSelector;
        this.productList = document.querySelector(productListSelector);
        if (!this.productList) {
            this.removeSkeletons();
            console.error('Product list element not found');
            return;
        }
        this.collectionId = this.productList.parentElement.dataset.collectionId;
        this.currentPage = 0;
        this.productsPerPage = parseInt(this.productList.parentElement.dataset.productsPerPage, 10) || 16;
        this.nostoInsertsCount = this.getNostoInsertsCount();
        this.api = null;

        this.init();
    }

    init() {
        window.onload = async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const hasFilter = Array.from(urlParams.keys()).some(key => key.startsWith('filter'));
                const hasSortBy = urlParams.has('sort_by');

                // Registered in both cases: a page opened with a filter or sort must get the Nosto grid back when they
                // are removed (the server no longer outputs the cards without filter or sort).
                this.setupFilter();
                this.setupPaginationClicks();

                // AUB-582: si un filtre ou un tri est appliqué, utiliser le système natif Shopify
                if (hasFilter || hasSortBy) {
                    this.showFallbackContent();
                } else {
                    await this.initializeNosto();
                    this.setupPagination();
                    this.setupSorting();
                }
            } catch (error) {
                console.error('Error initializing Nosto collection:', error);
            }
        };
    }

    removeSkeletons() {
        document.querySelectorAll('.skeleton').forEach(skeleton => skeleton.remove());
    }

    updateURLParameter(key, value) {
        const url = new URL(window.location);
        url.searchParams.set(key, value);
        window.history.pushState({}, '', url);
    }

    getURLParameter(name) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    }

    async checkElement(selector) {
        while (document.querySelector(selector) === null) {
            await new Promise(resolve => requestAnimationFrame(resolve))
        }
        return document.querySelector(selector);
    }

    getCachedData(key) {
        const cachedData = sessionStorage.getItem(key);
        if (!cachedData) {
            return null;
        }
        const { data, timestamp } = JSON.parse(cachedData);
        const now = new Date().getTime();
        // set 1 hour caching for performance purposes
        if (now - timestamp > 3600000) {
            sessionStorage.removeItem(key);
            return null;
        }
        return data;
    }

    setCachedData(key, data) {
        const timestamp = new Date().getTime();
        sessionStorage.setItem(key, JSON.stringify({ data, timestamp }));
    }

    getNostoInsertsCount() {
        const rawCount = parseInt(this.productList.parentElement.dataset.nostoInsertsCount, 10);
        if (![0, 1, 2, 3].includes(rawCount)) {
            return 0;
        }
        return rawCount;
    }

    async fetchUrls(size, from) {
        try {
            const response = await this.api.search({
                products: {
                    categoryId: this.collectionId,
                    fields: ['url'],
                    size: size,
                    from: from,
                }
            }, {
                track: 'category',
                redirect: true
            });

            const result = response || [];

            if (!result.products) {
                throw new Error('No products returned from Nosto API');
            }

            const urls = result.products?.hits?.map(product => product.url) || [];
            const handles = urls.map(url => {
                const parts = url.split('/');
                return parts[parts.length - 1];
            });

            const totalResults = result.products.total || 0;
            const totalPages = Math.ceil(totalResults / size);
            const currentPage = Math.floor(from / size) + 1;

            return {
                handles: handles.join(','),
                currentPage: currentPage,
                totalPages: totalPages,
                totalResults: totalResults,
            };
        } catch (error) {
            this.showFallbackContent();
            console.error('Error fetching URLs from Nosto API:', error);
            return {
                handles: '',
                currentPage: 0,
                totalPages: 0,
                totalResults: 0,
            };
        }
    }

    async loadProducts(page) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
        const firstPageSize = Math.max(1, this.productsPerPage - this.nostoInsertsCount);
        const pageSize = page === 1 ? firstPageSize : this.productsPerPage;
        const pageOffset = page === 1 ? 0 : Math.max(0, ((page - 1) * this.productsPerPage) - this.nostoInsertsCount);
        const cacheKey = `nostoProductHandlesPage${page}_Collection${this.collectionId}_PerPage${this.productsPerPage}_Inserts${this.nostoInsertsCount}`;
        let handlesParameter = this.getCachedData(cacheKey);
    
        let paginationData;
        if (!handlesParameter) {
            try {
                paginationData = await this.fetchUrls(pageSize, pageOffset);
                handlesParameter = paginationData.handles;
                this.totalPages = paginationData.totalResults <= firstPageSize
                    ? (paginationData.totalResults > 0 ? 1 : 0)
                    : 1 + Math.ceil((paginationData.totalResults - firstPageSize) / this.productsPerPage);
    
                if (!handlesParameter) {
                    console.error('Nosto: No handles to fetch');
                    this.showFallbackContent();
                    return false;
                }
    
                this.setCachedData(cacheKey, handlesParameter);
            } catch (error) {
                console.error('Error fetching URLs:', error);
                return false;
            }
        } else {
            paginationData = await this.fetchUrls(pageSize, pageOffset);
            this.totalPages = paginationData.totalResults <= firstPageSize
                ? (paginationData.totalResults > 0 ? 1 : 0)
                : 1 + Math.ceil((paginationData.totalResults - firstPageSize) / this.productsPerPage);
            if(handlesParameter !== paginationData.handles){
                handlesParameter = paginationData.handles;
            }
        }
    
        if (this.hasFilterOrSort()) return false;
        const paginationHtml = this.renderPagination(page, this.totalPages);
        const paginationContainer = document.querySelector('#pagination-nosto');
        if (paginationContainer) paginationContainer.innerHTML = paginationHtml;
    
        try {
            const urls = handlesParameter;
            const resultArray = urls.split(',');
            function isEmptyProductCard(cardHtml) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(cardHtml?.["product-card"], 'text/html');
                const productCard = doc.querySelector('product-card');
                if (!productCard) return true; 
                const dataProductId = productCard.getAttribute('data-product-id');
                return !dataProductId;
              }
              
            if (resultArray[0] !== "") {
                const promises = resultArray.map((url) => fetch(`${window.Shopify.routes.root}products/${url}?sections=product-card`).then(res => res.json()));
    
                Promise.all(promises).then((res) => {
                    // Filtrer le tableau pour supprimer les produits vides
                    const filteredProductCards = res.filter(
                      (product) => !isEmptyProductCard(product)
                    );

                    const combinedHTML = filteredProductCards.map(r => r['product-card'].replace(/^<div.*?class="shopify-section[^>]*?>/, "").replace(/<\/div>$/, "")).join("");
                    // A filter or sort applied meanwhile: the native grid owns the list now.
                    if (this.hasFilterOrSort()) return;
                    // The section may have been re-rendered (facets) while the cards were loading.
                    this.productList = document.querySelector(this._productListSelector) || this.productList;
                    this.productList.closest("#main").classList.add('nosto-loaded');
                    this.productList.innerHTML = combinedHTML;
                    this.productList.style.opacity = 1;
                    this.announceProductListRendered();
    
                    this.checkElement('product-card[reveal-on-scroll="true"]').then(() => {
                        const productCards = this.productList.querySelectorAll('product-card[reveal-on-scroll="true"]');
                        if (productCards)
                            productCards.forEach(card => card.setAttribute('is-nosto-product', ''));
                        this.revealCards(productCards);       
                                                
                        productCards.forEach(card => {
                         const anchor =  card.querySelector('a[data-instant]');
                         const url = window.location.origin + anchor.getAttribute('href');
                         card.addEventListener('click', (event) => {
                            setTimeout( () => {
                                nostojs(api => {
                                    api.recordSearchClick('category', {
                                        productId: card.dataset.productId,
                                        url: url,
                                    });
                                });
                            },200)
                         });
                        });

                    });
                }).catch((error) => {
                    // A product request failed (e.g. product unavailable in this market): without this the skeletons
                    // stayed forever. Keep the grid already shown (pagination), otherwise show the native grid.
                    console.error('Nosto: could not load the product cards', error);
                    if (this.productList.closest("#main").classList.contains('nosto-loaded')) {
                        this.productList.style.opacity = 1;
                    } else {
                        this.showFallbackContent();
                    }
                });
    
                document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
                return true;

            } else {
                console.error('Error: Invalid data structure', data);
                return false;
            }
          
        } catch (error) {
            console.error('Error during section render fetch:', error);
            return false;
        }
    }

    revealCards(elements) {
        elements.forEach((element, index) => {
            setTimeout(() => {
               if (index === 0) {
                  document.dispatchEvent(new Event('collection-rendred'));
                }
                element.classList.add('visible');
            }, index * 50 + 400);
        });
      }

    hasFilterOrSort() {
        const params = new URLSearchParams(window.location.search);
        return Array.from(params.keys()).some(key => key.startsWith('filter')) || params.has('sort_by');
    }

    setupFilter() {
        if (this._facetUpdateListenerAdded) return;
        this._facetUpdateListenerAdded = true;

        // AUB-582: gestion des filtres ET du tri (sort_by) — miroir de nosto-search.js
        // On "facet:update" the theme re-renders the whole section (grid, pagination) via AJAX, then dispatches
        // "collection-rendred". The grid is switched only once that new DOM is in place: switching on a timer raced
        // with the AJAX render, which then replaced the Nosto grid and its pagination (empty grid, dead pagination).
        document.addEventListener("facet:update", () => {
            this._listBeforeFacetUpdate = document.querySelector(this._productListSelector);
        });

        // AUB-574: After AJAX replaces the section DOM, refresh stale productList reference
        // and remove any skeletons re-injected by the server render
        document.addEventListener("collection-rendred", () => {
            const fresh = document.querySelector(this._productListSelector);
            // "collection-rendred" is also dispatched by this script (cards revealed, fallback shown): only a product
            // list different from the one in place when the facets changed means the section was re-rendered.
            const sectionReplaced = this._listBeforeFacetUpdate && fresh && fresh !== this._listBeforeFacetUpdate;
            if (fresh) this.productList = fresh;
            if (this.hasFilterOrSort()) {
                this.removeSkeletons();
            }
            if (!sectionReplaced) return;
            this._listBeforeFacetUpdate = null;
            this._serverCardsRequest = null;
            this.onFacetsRendered();
        });
    }

    onFacetsRendered() {
        const main = this.productList.closest("#main");
        if (this.hasFilterOrSort()) {
            // Filtered / sorted: the server rendered the native grid.
            this.showFallbackContent();
            const paginationContainer = document.querySelector('#pagination-nosto');
            if (paginationContainer) {
                paginationContainer.innerHTML = '';
            }
            return;
        }

        // Back to the Nosto grid: the server only rendered skeletons. Clear both states so the skeletons show while
        // Nosto loads, and so the native fallback can still run if Nosto fails.
        main.classList.remove('nosto-loaded', 'nosto-fallback');
        const shopifyPagination = document.querySelector('.shopify-pagination');
        if (shopifyPagination) {
            shopifyPagination.classList.add('hidden');
        }

        if (this.api) {
            this.currentPage = 1;
            this.loadProducts(this.currentPage);
        } else {
            this.initializeNosto().then(() => {
                this.setupPagination();
                this.setupSorting();
            });
        }
    }

    setupSorting() {
        localStorage.setItem("clear_sort", false);
        if (localStorage.getItem("clear_sort") === "false") {
            const sortOptions = document.querySelectorAll('.popover-listbox__option group');
            if (!sortOptions)
                return;
            sortOptions?.forEach(function (opt) {
                if (opt.checked) opt.removeAttribute('checked');
            });
            if (location.href.includes('?sort_by')) {
                history.pushState({}, null, location.href.split('?')[0]);
            }
            localStorage.setItem("clear_sort", true);
        }
    }

    setupPagination() {
        let paginationContainer = document.querySelector('#pagination-nosto');
        if (!paginationContainer) {
            paginationContainer = document.createElement('div');
            paginationContainer.id = 'pagination-nosto';
            this.productList.parentElement.appendChild(paginationContainer);
        }
    
        this.currentPage = this.getURLParameter('page') ? parseInt(this.getURLParameter('page'), 10) : 1;
        this.currentPage = isNaN(this.currentPage) || this.currentPage < 1 ? 1 : this.currentPage;
    
        const totalPages = this.totalPages || 1;
    
        this.renderPagination(this.currentPage, totalPages);
    }

    // Delegated once on the document: the theme replaces #pagination-nosto on every facet re-render, so a listener on
    // the element itself was lost after a filter was applied or removed.
    setupPaginationClicks() {
        if (this._paginationClicksAdded) return;
        this._paginationClicksAdded = true;

        document.addEventListener('click', async (event) => {
            const link = event.target.closest?.('#pagination-nosto a');
            if (!link) return;
            event.preventDefault();

            const page = parseInt(link.getAttribute('data-page'), 10);
            if (page && page !== this.currentPage) {
                this.currentPage = page;
                this.productList.style.opacity = 0.4;

                const success = await this.loadProducts(this.currentPage);
                if (success) {
                    this.updateURLParameter('page', this.currentPage);
                    document.querySelector(".collection").scrollIntoView({ block: "start", behavior: "smooth" });
                }
            }
        });
    }
    
    renderPagination(currentPage, totalPages) {
        let paginationHtml = '';
    
        if (totalPages <= 1) return paginationHtml; 

        const rightArrowSvg = `
        <svg aria-hidden="true" focusable="false" fill="none" width="11" class="icon icon-chevron-right  icon--direction-aware" viewBox="0 0 10 10">
            <path d="m3 9 4-4-4-4" stroke="currentColor" stroke-linecap="square"></path>
        </svg>`;

         const leftArrowSvg = `
        <svg aria-hidden="true" focusable="false" fill="none" width="11" class="icon icon-chevron-left  icon--direction-aware" viewBox="0 0 10 10">
            <path d="m7 9-4-4 4-4" stroke="currentColor" stroke-linecap="square"></path>
        </svg>`;
    
        if (currentPage > 1) {
            paginationHtml += `<a href="#" data-page="${currentPage - 1}" class="pagination__link h6 pagination-prev">${leftArrowSvg}</a>`;
        }
    
        const range = 2;
        let startPage, endPage;
    
        if (totalPages <= 2 * range + 1) {
            startPage = 1;
            endPage = totalPages;
        } else {
            if (currentPage <= range) {
                startPage = 1;
                endPage = range * 2 + 1;
            } else if (currentPage + range >= totalPages) {
                startPage = totalPages - range * 2;
                endPage = totalPages;
            } else {
                startPage = currentPage - range;
                endPage = currentPage + range;
            }
        }
    
        if (startPage > 1) {
            paginationHtml += `<a href="#" data-page="1" class="pagination__link h6 pagination-page">1</a>`;
            if (startPage > 2) paginationHtml += `<a href="#" class="pagination__link h6 pagination__ellipsis">...</a>`;
        }
    
        for (let i = startPage; i <= endPage; i++) {
            paginationHtml += `<a href="#" data-page="${i}" ${i === currentPage ? 'aria-current="page"' : ''} class="pagination__link h6 pagination-page ${i === currentPage ? 'pagination__link--disabled' : ''}">${i}</a>`;
        }
    
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) paginationHtml += `<a href="#" class="pagination__link h6 pagination__ellipsis">...</a>`;
            paginationHtml += `<a href="#" data-page="${totalPages}" class="pagination__link h6 pagination-page">${totalPages}</a>`;
        }
    
        if (currentPage < totalPages) {
            paginationHtml += `<a href="#" data-page="${currentPage + 1}" class="pagination__link h6 pagination-next">${rightArrowSvg}</a>`;
        }
    
        return paginationHtml;
    }
    
    

    async initializeNosto() {
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Nosto API timeout after 5s')), 5000)
        );

        const nostoPromise = new Promise((resolve, reject) => {
            nostojs(async nostoApi => {
                try {
                    console.info('Nosto API initialized');
                    this.api = nostoApi;
                    this.api.loadRecommendations();

                    const pageParam = this.getURLParameter('page');
                    if (pageParam) {
                        this.currentPage = parseInt(pageParam, 10);
                    } else {
                        this.currentPage = isNaN(this.currentPage) || this.currentPage < 1 ? 1 : this.currentPage;
                    }

                    const success = await this.loadProducts(this.currentPage);
                    if (!success) {
                        this.currentPage = 1;
                        await this.loadProducts(this.currentPage);
                    }
                    resolve();
                } catch (err) {
                    reject(err);
                }
            });
        });

        try {
            await Promise.race([nostoPromise, timeoutPromise]);
        } catch (error) {
            console.error('Error initializing Nosto API:', error);
            this.showFallbackContent();
        }
    }


async showFallbackContent() {
    this.productList = document.querySelector(this._productListSelector) || this.productList;
    // Without filter or sort the server no longer outputs the cards on this template (sections/main-collection.liquid):
    // load them first, the skeletons stay in place meanwhile.
    if (!this.productList.querySelector(':scope > product-card')) {
        await this.loadServerCards();
        // Nosto rendered meanwhile: keep its grid.
        if (this.productList.closest('#main').classList.contains('nosto-loaded')) return;
    }
    this.removeSkeletons();
    this.productList.closest("#main").classList.add('nosto-fallback');
    this.productList.style.opacity = 1;
    // Their Swipers are not created while they are hidden (swiper-mobile.js).
    document.dispatchEvent(new Event('collection-rendred'));
    this.announceProductListRendered();
}

// Native grid of this section, rendered with the collection's default sort: an explicit sort_by makes the section output
// the full cards (same order as without it). Several fallback triggers can fire (timeout, API error): one request.
loadServerCards() {
    this._serverCardsRequest ||= (async () => {
        try {
            const sectionId = this.productList.id.replace(/^product-list-/, '');
            const url = new URL(window.location.href);
            url.searchParams.set('section_id', sectionId);
            if (!url.searchParams.has('sort_by')) {
                url.searchParams.set('sort_by', this.productList.parentElement.dataset.defaultSortBy || 'manual');
            }
            const response = await fetch(url.toString());
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const serverList = new DOMParser().parseFromString(await response.text(), 'text/html').getElementById(this.productList.id);
            if (!serverList) throw new Error('product list not found in section response');
            if (this.productList.closest('#main').classList.contains('nosto-loaded')) return false;

            // ProductList only starts the reveal-on-scroll animation for the cards present when it connects.
            serverList.querySelectorAll('[reveal-on-scroll]').forEach(element => element.removeAttribute('reveal-on-scroll'));
            this.productList.replaceChildren(...serverList.childNodes);
            return true;
        } catch (error) {
            console.error('Nosto fallback: could not load the collection products', error);
            this._serverCardsRequest = null;
            return false;
        }
    })();
    return this._serverCardsRequest;
}

// The grid is complete (snippets/datalayer.liquid builds view_item_list from the displayed products). The flag covers
// listeners registered later: with a filter or sort in the URL, the native grid is ready before DOMContentLoaded.
announceProductListRendered() {
    if (window.nostoProductListRendered) return;
    window.nostoProductListRendered = true;
    document.dispatchEvent(new CustomEvent('nosto:product-list-rendered'));
}

}

new NostoCollection('product-list[is-nosto-collection]');
